import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { AscButton, AscProgressBar } from "../../components";
import AscCalendarDatePicker from "../../components/AscCalendarDatePicker/AscCalendarDatePicker";
import AscOptionsSelector from "../../components/AscOptionsSelector/AscOptionsSelector";
import AscHeading from "../../components/AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../components/AscTypography/AscParagraph/AscParagraph";
import {
  DEFAULT_DEMO_DATE_QUESTION,
  DEFAULT_DEMO_QUESTION,
  DEFAULT_GOAL_QUESTION,
  DEFAULT_IN_TOUCH_PROMPT,
} from "../../lib";
import AscAuthFlowLayout from "../AscAuthFlowLayout/AscAuthFlowLayout";
import styles from "./AscSchoolQuestionsView.module.scss";

export interface AscSchoolQuestionsViewProps {
  className?: string;
  demoQuestion?: {
    title: string;
    description: string;
    options: {
      value: boolean;
      label: string;
      icon: React.ReactNode;
    }[];
  };
  goalQuestion?: {
    title: string;
    description: string;
    options: Record<"value" | "label", string>[];
  };
  demoDateQuestion?: Record<"title" | "description", string>;
  inTouchPrompt?: Record<"title" | "description", string>;
  loading?: boolean;
  onFinish?: (questionsResponses: {
    isDemoRequired: boolean;
    goal: string;
    firstDemoDate: string;
    secondDemoDate: string;
  }) => void;
  totalSteps?: number;
  initialStep?: number;
  disabled?: boolean;
}

export default function AscSchoolQuestionsView({
  className,
  demoQuestion = DEFAULT_DEMO_QUESTION,
  goalQuestion = DEFAULT_GOAL_QUESTION,
  demoDateQuestion = DEFAULT_DEMO_DATE_QUESTION,
  inTouchPrompt = DEFAULT_IN_TOUCH_PROMPT,
  totalSteps = 4,
  initialStep = 1,
  onFinish,
  loading,
  disabled = false,
}: AscSchoolQuestionsViewProps) {
  const [step, setStep] = useState<"demo" | "goal" | "demo-date" | "in-touch">(
    "demo"
  );
  const [stepCount, setStepCount] = useState(initialStep);
  const [demoResponse, setDemoResponse] = useState<boolean>();
  const [goalResponse, setGoalResponse] = useState<string>();
  const [demoDates, setDemoDates] = useState<string[]>();

  function handleBackClick() {
    if (step === "goal") {
      setStep("demo");
      setStepCount(initialStep);
      return;
    }
    if (step === "demo-date") {
      setStep("goal");
      setStepCount(initialStep + 1);
      return;
    }
    if (step === "in-touch") {
      setStep("demo-date");
      setStepCount(initialStep + 2);
      return;
    }
  }

  const handleSelectDemoResponse = useCallback(
    function (demoResponse: string) {
      setDemoResponse(demoResponse === "true");
      setStep("goal");
      setStepCount(initialStep + 1);
    },
    [initialStep]
  );

  function handleContinueClick() {
    if (demoResponse === undefined || !goalResponse) {
      return;
    }
    if (step === "goal") {
      setStep("demo-date");
      setStepCount(initialStep + 2);
      return;
    }
    if (!demoDates?.length || demoDates?.length < 2) {
      return;
    }
    if (step === "demo-date") {
      setStep("in-touch");
      setStepCount(initialStep + 3);
      return;
    }
    if (step === "in-touch") {
      onFinish?.({
        isDemoRequired: demoResponse === demoQuestion?.options?.[1]?.value,
        goal: goalResponse,
        firstDemoDate: demoDates?.[0],
        secondDemoDate: demoDates?.[1],
      });
    }
  }

  function shouldDisableContinueButton() {
    if (step === "goal") {
      return !goalResponse;
    }
    if (step === "demo-date") {
      return !demoDates?.length || demoDates?.length < 2;
    }
    return false;
  }

  const currentQuestion = useMemo(() => {
    switch (step) {
      case "demo":
        return (
          <>
            <header className={styles.heading}>
              <AscHeading className={styles.title} level={1}>
                {demoQuestion?.title}
              </AscHeading>
              <AscParagraph className={styles.subTitle}>
                {demoQuestion?.description}
              </AscParagraph>
            </header>

            <AscOptionsSelector
              className={styles.optionsSelector}
              size="large"
              value={`${demoResponse}`}
              onChange={handleSelectDemoResponse}
            >
              {demoQuestion?.options?.map((response) => (
                <AscOptionsSelector.Option
                  key={`${response?.value}`}
                  label={response?.label}
                  value={`${response?.value}`}
                  icon={response?.icon}
                />
              ))}
            </AscOptionsSelector>
          </>
        );
      case "goal":
        return (
          <>
            <header className={styles.heading}>
              <AscHeading className={styles.title} level={1}>
                {goalQuestion?.title}
              </AscHeading>
              <AscParagraph className={styles.subTitle}>
                {goalQuestion?.description}
              </AscParagraph>
            </header>

            <AscOptionsSelector
              className={styles.optionsSelector}
              hideArrowIcon
              value={goalResponse}
              onChange={setGoalResponse}
            >
              {goalQuestion?.options?.map((response) => (
                <AscOptionsSelector.Option
                  key={response?.value}
                  label={response?.label}
                  value={response?.value}
                />
              ))}
            </AscOptionsSelector>
          </>
        );
      case "demo-date":
        return (
          <>
            <header className={styles.heading}>
              <AscHeading className={styles.title} level={1}>
                {demoDateQuestion?.title}
              </AscHeading>
              <AscParagraph className={styles.subTitle}>
                {demoDateQuestion?.description}
              </AscParagraph>
            </header>
            <AscCalendarDatePicker
              className={styles.datePicker}
              daysToSelect={2}
              value={demoDates}
              onChange={setDemoDates}
            />
          </>
        );
      case "in-touch":
        return (
          <header className={styles.heading}>
            <AscHeading className={styles.title} level={1}>
              {inTouchPrompt?.title}
            </AscHeading>
            <AscParagraph className={styles.subTitle}>
              {inTouchPrompt?.description}
            </AscParagraph>
          </header>
        );
      default:
        return null;
    }
  }, [
    step,
    demoQuestion,
    demoResponse,
    demoDateQuestion,
    goalResponse,
    goalQuestion,
    handleSelectDemoResponse,
    inTouchPrompt,
    demoDates,
  ]);

  return (
    <AscAuthFlowLayout alignment="right" roundSideImage>
      <div
        data-testid="asc-school-questions-view"
        className={classNames(
          styles.ground,
          {
            [styles.demo]: step === "demo",
            [styles.goal]: step === "goal",
            [styles.demoDate]: step === "demo-date",
          },
          className
        )}
      >
        <div className={styles.content}>
          {currentQuestion}

          <div className={styles.navigationContainer}>
            <AscButton
              className={classNames(styles.footerButton, styles.backButton)}
              variant="secondary"
              size="large"
              onClick={handleBackClick}
              disabled={loading}
            >
              Back
            </AscButton>
            <AscButton
              className={classNames(styles.footerButton, styles.continueButton)}
              variant="primary"
              size="large"
              onClick={handleContinueClick}
              disabled={shouldDisableContinueButton()}
              loading={loading || disabled}
            >
              Continue
            </AscButton>
          </div>
        </div>

        <footer className={styles.footer}>
          <AscProgressBar
            className={styles.progressBar}
            type="steps"
            value={stepCount}
            max={totalSteps}
          />
        </footer>
      </div>
    </AscAuthFlowLayout>
  );
}
